"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormFieldEditor = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const mobx_1 = require("mobx");
const image_button_1 = require("../../../components/button/image.button");
const icon_1 = require("../../../components/icon");
const icons_1 = require("../../../components/icon/icons");
const input_1 = require("../../../components/input/input");
const input_checkbox_1 = require("../../../components/input/input.checkbox");
const input_state_group_1 = require("../../../components/input/input.state.group");
const menu_1 = require("../../../components/menu/menu");
const menu_item_1 = require("../../../components/menu/menu.item");
const modal_service_1 = require("../../../components/modal/modal.service");
const notification_service_1 = require("../../../components/notification.toast/notification.service");
const theme_1 = require("../../../components/theme");
const i18n_1 = require("../../../i18n");
const mithril_1 = require("../../../mithril");
const services_1 = require("../../../services");
const form_palette_1 = require("../form.palette");
const form_field_tree_view_1 = require("./form.field.tree.view");
const form_info_panel_style_1 = require("./form.info.panel.style");
(0, theme_1.addStyles)(form_info_panel_style_1.Style);
class FormFieldEditor {
    constructor(designer, field) {
        this.state = new input_state_group_1.IkeInputStateGroup();
        this.designer = designer;
        this.field = field;
        this.state.isLabelShown = false;
        this.menu = new menu_1.MenuComponent({
            items: () => this.viewMenuItems(),
            className: form_info_panel_style_1.CSS.menu,
        });
    }
    get isLabelable() {
        switch (this.field.type) {
            case core_api_1.IkeFieldType.TEXT:
            case core_api_1.IkeFieldType.NUMBER:
            case core_api_1.IkeFieldType.SELECT_LIST:
            case core_api_1.IkeFieldType.NESTED_LIST:
                return true;
            default:
                return false;
        }
    }
    get isCloneable() {
        switch (this.field.type) {
            case core_api_1.IkeFieldType.DATE:
            case core_api_1.IkeFieldType.FORM:
            case core_api_1.IkeFieldType.NESTED_LIST:
            case core_api_1.IkeFieldType.NUMBER:
            case core_api_1.IkeFieldType.SELECT_LIST:
            case core_api_1.IkeFieldType.TEXT:
            case core_api_1.IkeFieldType.YES_NO:
                return true;
            default:
                return false;
        }
    }
    get isStatusable() {
        const validFieldTypes = [core_api_1.IkeFieldType.NESTED_LIST, core_api_1.IkeFieldType.SELECT_LIST];
        return this.field.isRoot && validFieldTypes.includes(this.field.type);
    }
    get isMapColorable() {
        const validFieldTypes = [core_api_1.IkeFieldType.NESTED_LIST, core_api_1.IkeFieldType.SELECT_LIST];
        return this.field.isRoot && validFieldTypes.includes(this.field.type);
    }
    get isAddressable() {
        const validFieldTypes = [core_api_1.IkeFieldType.TEXT, core_api_1.IkeFieldType.NUMBER];
        return validFieldTypes.includes(this.field.type);
    }
    get isRequirable() {
        const invalidFieldTypes = [
            core_api_1.IkeFieldType.FORM,
            core_api_1.IkeFieldType.VERTICAL_SEPARATION,
            core_api_1.IkeFieldType.YES_NO,
            core_api_1.IkeFieldType.FILE_TOOL,
        ];
        return this.field.isRoot && !invalidFieldTypes.includes(this.field.type);
    }
    get depth() {
        let depth = 0;
        let parent = this.field;
        while (parent != null) {
            parent = parent.parent;
            depth += 1;
        }
        return depth;
    }
    deleteField() {
        const parent = this.field.parent;
        this.field.Mutator.delete();
        this.state.destroy();
        this.designer.setSelected(parent);
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:delete');
    }
    addField() {
        const field = this.field;
        if (!core_shared_1.FormModelFieldSubForm.isSubForm(field)) {
            return;
        }
        const newField = field.createField(core_api_1.IkeFieldType.TEXT);
        newField.Mutator.adminOnly(field.adminOnly);
        newField.Mutator.collector(field.collector);
        newField.Mutator.required(field.required);
        newField.Mutator.annotationLabelFieldId(field.annotationLabelFieldId);
        newField.Mutator.clearanceThresholdInMeters(field.clearanceThresholdInMeters);
        this.designer.setSelected(newField);
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:tree:add', core_api_1.IkeFieldType.TEXT);
    }
    changeFieldType(type) {
        const originalType = this.field.type;
        if (type === originalType) {
            return;
        }
        if (core_shared_1.FormModelFieldSelectList.isSelectList(this.field) &&
            this.field.options != null &&
            this.field.options.length > 1) {
            notification_service_1.NotificationService.warn({ key: i18n_1.T.FORM_FIELD_CANNOT_CHANGE });
            return;
        }
        if (core_shared_1.FormModelFieldNestedList.isNestedList(this.field) &&
            this.field.options != null &&
            this.field.options.values.length > 0) {
            notification_service_1.NotificationService.warn({ key: i18n_1.T.FORM_FIELD_CANNOT_CHANGE });
            return;
        }
        if (core_shared_1.FormModelFieldSubForm.isSubForm(this.field) && this.field.fields != null && this.field.fields.length > 0) {
            notification_service_1.NotificationService.warn({ key: i18n_1.T.FORM_FIELD_CANNOT_CHANGE });
            return;
        }
        const parent = this.field.parent;
        const index = parent.getFieldIndex(this.field.id);
        const newField = parent.Mutator.create(type, null, index);
        newField.Mutator.displayName(this.field.displayName);
        newField.Mutator.collector(this.field.collector);
        newField.Mutator.adminOnly(this.field.adminOnly);
        newField.Mutator.annotationLabelFieldId(this.field.annotationLabelFieldId);
        newField.Mutator.clearanceThresholdInMeters(this.field.clearanceThresholdInMeters);
        if (newField.isCloneable) {
            newField.Mutator.cloneable(this.field.cloneable);
        }
        this.field.Mutator.delete();
        this.state.destroy();
        this.designer.setSelected(newField);
        services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:change-type', `${originalType} -> ${type}`);
    }
    /** View the field as a tree */
    viewTree() {
        return form_field_tree_view_1.FormFieldTreeView.view(this);
    }
    /** View the field as a info panel */
    viewInfo() {
        if (this.designer.isEditing) {
            return this._viewInfoRW();
        }
        return this._viewInfoRO();
    }
    // Additional information view for children;
    viewInfoRW() {
        return null;
    }
    viewInfoRO() {
        return null;
    }
    _viewInfoRO() {
        return (0, mithril_1.m)('div', { className: form_info_panel_style_1.CSS.container }, [this.viewTitle(), this.viewOptions(), this.viewInfoRO()]);
    }
    _viewInfoRW() {
        return (0, mithril_1.m)('div', { className: form_info_panel_style_1.CSS.container }, [
            this.viewFieldMove(),
            this.viewTitle(),
            this.viewOptions(),
            this.viewInfoRW(),
        ]);
    }
    viewTitle() {
        return (0, mithril_1.m)('div', { className: `${form_info_panel_style_1.CSS.section.container}` }, [
            input_1.Input.view(this.getTitleState()),
            this.viewFieldTypeButton(),
            this.viewFieldDeleteButton(),
        ]);
    }
    viewFieldTypeButton() {
        return (0, mithril_1.m)('div', { className: form_info_panel_style_1.CSS.container }, [
            (0, mithril_1.m)('button', {
                className: `mdl-button ${form_info_panel_style_1.CSS.type}`,
                title: (0, i18n_1.Translate)(i18n_1.T.FORM_FIELD_TYPE),
                disabled: this.field.isLocked,
                onclick: this.menu.show,
            }, [
                form_palette_1.FormDesignerPalette.viewIcon(this.field.type),
                form_palette_1.FormDesignerPalette.translate(this.field.type),
                icon_1.Icon.view(icons_1.MaterialIcon.KEY_DOWN),
            ]),
            this.menu.view(),
        ]);
    }
    viewFieldDeleteButton() {
        if (!this.designer.isEditing || this.field.isLocked) {
            return null;
        }
        return image_button_1.ImageButton.view(icons_1.MaterialIcon.DELETE, {
            title: i18n_1.T.FORM_FIELD_DELETE,
            onclick: () => this.confirmDeleteField(),
        });
    }
    viewOptions() {
        const canSetAdminOnly = services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE);
        const canSetStatusProperty = (services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE) || services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_SORT_STATUS)) &&
            this.isStatusable;
        const canSetMapColorProperty = services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_COLOR_FLAG) && this.isMapColorable;
        const canSetAddressProperty = services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE) && this.isAddressable;
        if (!this.isLabelable &&
            !this.isCloneable &&
            !canSetAdminOnly &&
            !canSetStatusProperty &&
            !canSetMapColorProperty &&
            !canSetAddressProperty) {
            return null;
        }
        const canSetRequiredProperty = this.isRequirable && services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE);
        const isRoot = this.field.isRoot;
        const canSecondaryLabel = (services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_SECONDARY_LABEL) ||
            services_1.Services.Token.has(core_api_1.IkeToken.FEATURE_GROUP_IKE_ANALYZE)) &&
            this.isLabelable &&
            isRoot;
        return (0, mithril_1.m)('div', { className: `${form_info_panel_style_1.CSS.section.container} ${form_info_panel_style_1.CSS.section.final}` }, [
            this.isLabelable
                ? this.viewOption(i18n_1.T.FORM_OPTION_LABEL, input_checkbox_1.CheckBox.view(this.getLabelableState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_LABEL_TITLE, { isRoot }))
                : null,
            canSecondaryLabel
                ? this.viewOption(i18n_1.T.FORM_OPTION_SECONDARY_LABEL, input_checkbox_1.CheckBox.view(this.getSecondarylabelableState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_SECONDARY_LABEL))
                : null,
            this.isCloneable
                ? this.viewOption(i18n_1.T.FORM_OPTION_CLONEABLE, input_checkbox_1.CheckBox.view(this.getCloneableState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_CLONEABLE_TITLE))
                : null,
            this.viewOption(i18n_1.T.FORM_OPTION_HIDE, input_checkbox_1.CheckBox.view(this.getHiddenState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_HIDE_TITLE)),
            canSetAdminOnly
                ? this.viewOption(i18n_1.T.FORM_OPTION_ADMIN_ONLY, input_checkbox_1.CheckBox.view(this.getAdminOnlyState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_ADMIN_ONLY_TITLE))
                : null,
            canSetStatusProperty
                ? this.viewOption(i18n_1.T.FORM_OPTION_STATUS, input_checkbox_1.CheckBox.view(this.getStatusState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_STATUS_TITLE))
                : null,
            canSetMapColorProperty
                ? this.viewOption(i18n_1.T.FORM_OPTION_MAP_COLOR, input_checkbox_1.CheckBox.view(this.getMapColorState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_MAP_COLOR_TITLE))
                : null,
            canSetRequiredProperty
                ? this.viewOption(i18n_1.T.FORM_OPTION_REQUIRED, input_checkbox_1.CheckBox.view(this.getRequiredState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_REQUIRED_TITLE))
                : null,
            canSetAddressProperty
                ? this.viewOption(i18n_1.T.FORM_OPTION_ADDRESS, input_checkbox_1.CheckBox.view(this.getAddressState()), (0, i18n_1.Translate)(i18n_1.T.FORM_OPTION_ADDRESS_TITLE))
                : null,
        ]);
    }
    viewOption(name, value, title) {
        return (0, mithril_1.m)('div', { className: form_info_panel_style_1.CSS.input.container, title }, [
            (0, mithril_1.m)('div', { className: form_info_panel_style_1.CSS.input.title }, (0, i18n_1.Translate)(name)),
            (0, mithril_1.m)('div', { className: form_info_panel_style_1.CSS.input.value }, value),
        ]);
    }
    confirmDeleteField() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const result = yield modal_service_1.ModalService.open({
                title: (0, i18n_1.Translate)(i18n_1.T.FORM_FIELD_DELETE_TITLE),
                content: (0, i18n_1.Translate)(i18n_1.T.FORM_FIELD_DELETE_CONFIRM, {
                    name: this.field.displayName || form_palette_1.FormDesignerPalette.translate(this.field.type),
                }),
                hasCancel: true,
                okName: i18n_1.T.FORM_FIELD_DELETE,
            });
            if (result.ok) {
                // add stateValue.displayName to empty fields
                // to handle the state issue
                if (this.field.stateValue.displayName === '') {
                    this.field.stateValue.displayName = 'empty';
                }
                // if this is either a status, mapColor or address field, delete it from the metadata as well
                const metaStatus = this.field.getForm().metadata.getStatusField();
                if (metaStatus && metaStatus.id === this.field.id) {
                    this.field.getForm().metadata.removeStatusField(this.field);
                }
                const metaMapColor = this.field.getForm().metadata.getMapColorField();
                if (metaMapColor && metaMapColor.id === this.field.id) {
                    this.field.getForm().metadata.removeMapColorField(this.field);
                }
                const metaAddressLookup = this.field.getForm().metadata.getAddressLookupField();
                if (metaAddressLookup && metaAddressLookup.id === this.field.id) {
                    this.field.getForm().metadata.removeAddressLookupField(this.field);
                }
                this.designer.setSelected(this.field.parent);
                this.state.destroy();
                this.field.Mutator.delete();
            }
        });
    }
    viewFieldMove() {
        if (!this.designer.isEditing || this.field.parent == null) {
            return null;
        }
        const index = this.field.parent.getFieldIndex(this.field.id);
        const fieldCount = this.field.parent.fields.length;
        const isFirst = index === 0;
        const isLast = index === fieldCount - 1;
        return (0, mithril_1.m)('div', { className: form_info_panel_style_1.CSS.move }, [
            image_button_1.ImageButton.view(icons_1.MaterialIcon.KEY_UP, {
                onclick: () => {
                    if (isFirst) {
                        return;
                    }
                    this.field.Mutator.moveUp();
                    this.designer.setScrollToField(this.field);
                    services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:move-up', this.field.id);
                },
                title: i18n_1.T.FORM_FIELD_MOVE_UP,
                disabled: isFirst,
                className: image_button_1.ImageButton.CSS.isSmall,
            }),
            image_button_1.ImageButton.view(icons_1.MaterialIcon.KEY_DOWN, {
                onclick: () => {
                    if (isLast) {
                        return;
                    }
                    this.field.Mutator.moveDown();
                    this.designer.setScrollToField(this.field);
                    services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:move-down', this.field.id);
                },
                title: i18n_1.T.FORM_FIELD_MOVE_DOWN,
                disabled: isLast,
                className: image_button_1.ImageButton.CSS.isSmall,
            }),
        ]);
    }
    viewMenuItems() {
        const subformDepth = this.depth - 1;
        const items = [];
        for (const key of Object.keys(form_palette_1.FIELD_TYPE_DISPLAY)) {
            const option = form_palette_1.FIELD_TYPE_DISPLAY[key];
            const maxDepthReached = option.maxDepth != null && option.maxDepth < subformDepth;
            const hasPermission = services_1.Services.User.hasFormPermission(key);
            if (hasPermission) {
                items.push({
                    view: menu_item_1.MenuItemComponent.view((0, i18n_1.Translate)(option.title), option.icon),
                    disabled: key === this.field.type || maxDepthReached || !hasPermission,
                    action: () => this.changeFieldType(key),
                });
            }
        }
        return items;
    }
    getTitleState() {
        let displayState = this.state.get('display');
        if (displayState == null) {
            displayState = this.state.child(i18n_1.T.FORM_FIELD_DISPLAY_NAME, 'display');
            displayState.isLabelShown = true;
            displayState.isRequired = true;
            displayState.onValue((val) => this.field.Mutator.displayName(val));
            displayState.setValue(this.field.displayName);
            displayState.setAutoFocus(true);
        }
        const form = this.field.getForm();
        let isSpanSubformFieldOrSubField = false;
        const formSpanSubForm = form.spanSubForm;
        if (formSpanSubForm) {
            let spanSubFormSubFieldIds = [];
            if (formSpanSubForm.subFormSpanTypeState) {
                spanSubFormSubFieldIds = Object.values(formSpanSubForm.subFormSpanTypeState);
            }
            // if this field is span subform field, or is a span subform subfield, we can still edit the field name
            isSpanSubformFieldOrSubField =
                formSpanSubForm.id === this.field.id || spanSubFormSubFieldIds.includes(this.field.id);
        }
        displayState.readOnly = (this.field.isLocked && !isSpanSubformFieldOrSubField) || !this.designer.isEditing;
        return displayState;
    }
    getLabelableState() {
        return {
            isSelected: () => this.field.title,
            setSelected: (val) => {
                this.setTitle(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:label', String(val));
            },
            isDisabled: !this.designer.isEditing,
        };
    }
    getSecondarylabelableState() {
        return {
            isSelected: () => this.field.subtitle,
            setSelected: (val) => {
                this.setSubtitle(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:secondarylabel', String(val));
            },
            isDisabled: !this.designer.isEditing,
        };
    }
    getHiddenState() {
        return {
            isSelected: () => this.field.collector === core_api_1.IkeCollectorType.OFFICE || this.isParentHidden(),
            setSelected: (val) => {
                this.setHidden(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:hidden', String(val));
            },
            isDisabled: !this.designer.isEditing || this.isParentHidden(),
        };
    }
    getAdminOnlyState() {
        return {
            isSelected: () => this.field.adminOnly === true || this.isParentAdminOnly(),
            setSelected: (val) => {
                this.setAdminOnly(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:adminOnly', String(val));
            },
            isDisabled: !this.designer.isEditing || this.isParentAdminOnly(),
        };
    }
    getCloneableState() {
        return {
            isSelected: () => this.field.cloneable != null && this.field.cloneable !== core_api_1.IkeCloneable.NONE,
            setSelected: (val) => {
                this.setCloneable(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:cloneable', String(val));
            },
            isDisabled: !this.designer.isEditing || !this.isParentCloneable(),
        };
    }
    getStatusState() {
        return {
            isSelected: () => this.field.isStatusField,
            setSelected: (val) => {
                this.setStatusField(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:status', String(val));
            },
            isDisabled: !this.designer.isEditing || this.isParentHidden(),
        };
    }
    getMapColorState() {
        return {
            isSelected: () => this.field.isMapColorField,
            setSelected: (val) => {
                this.setMapColorField(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:mapcolor', String(val));
            },
            isDisabled: !this.designer.isEditing || this.isParentHidden(),
        };
    }
    getRequiredState() {
        return {
            isSelected: () => this.field.required === true,
            setSelected: (val) => {
                this.setRequired(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:required', String(val));
            },
            isDisabled: !this.designer.isEditing || this.isRequiredDisabled(),
        };
    }
    getAddressState() {
        return {
            isSelected: () => this.field.isAddressLookupField,
            setSelected: (val) => {
                this.setAddressLookupField(val);
                services_1.Services.Analytics.trackEvent(services_1.Services.Analytics.EventCategory.Mutate, 'form:field:address', String(val));
            },
            isDisabled: !this.designer.isEditing || this.isParentHidden(),
        };
    }
    isParentCloneable() {
        if (this.field.parent.parent == null) {
            return true;
        }
        return this.field.parent.cloneable != null && this.field.parent.cloneable !== core_api_1.IkeCloneable.NONE;
    }
    isParentAdminOnly() {
        if (this.field.parent.parent == null) {
            return false;
        }
        return this.field.parent.adminOnly;
    }
    isParentHidden() {
        if (this.field.parent.parent == null) {
            return false;
        }
        return this.field.parent.collector === core_api_1.IkeCollectorType.OFFICE;
    }
    isRequiredDisabled() {
        const adminField = this.field.adminOnly;
        const hiddenField = this.field.collector === core_api_1.IkeCollectorType.OFFICE;
        return adminField || hiddenField;
    }
    setTitle(title) {
        this.field.Mutator.title(title);
    }
    setSubtitle(subtitle) {
        this.field.Mutator.subtitle(subtitle);
    }
    setHidden(hidden) {
        if (hidden) {
            this.field.Mutator.collector(core_api_1.IkeCollectorType.OFFICE);
            this.field.Mutator.required(false);
        }
        else {
            this.field.Mutator.collector(core_api_1.IkeCollectorType.ALL);
        }
    }
    setAdminOnly(adminOnly) {
        this.field.Mutator.adminOnly(adminOnly);
        if (adminOnly) {
            this.field.Mutator.required(false);
        }
    }
    setCloneable(cloneable) {
        if (cloneable) {
            this.field.Mutator.cloneable(core_api_1.IkeCloneable.FIRST);
        }
        else {
            this.field.Mutator.cloneable(core_api_1.IkeCloneable.NONE);
        }
    }
    setAnnotationLabelFieldId(fieldId) {
        this.field.Mutator.annotationLabelFieldId(fieldId);
    }
    setClearanceThresholdInMeters(clearanceThresholdInMeters) {
        this.field.Mutator.clearanceThresholdInMeters(clearanceThresholdInMeters);
    }
    setStatusField(useAsStatus) {
        this.field.Mutator.isStatusField(useAsStatus);
    }
    setMapColorField(useAsMapColor) {
        this.field.Mutator.isMapColorField(useAsMapColor);
    }
    setRequired(required) {
        this.field.Mutator.required(required);
    }
    setAddressLookupField(address) {
        this.field.Mutator.isAddressLookupField(address);
    }
}
exports.FormFieldEditor = FormFieldEditor;
tslib_1.__decorate([
    mobx_1.action.bound
], FormFieldEditor.prototype, "deleteField", null);
tslib_1.__decorate([
    mobx_1.action.bound
], FormFieldEditor.prototype, "addField", null);
tslib_1.__decorate([
    mobx_1.action.bound
], FormFieldEditor.prototype, "changeFieldType", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setTitle", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setSubtitle", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setHidden", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setAdminOnly", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setCloneable", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setAnnotationLabelFieldId", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setClearanceThresholdInMeters", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setStatusField", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setMapColorField", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setRequired", null);
tslib_1.__decorate([
    mobx_1.action
], FormFieldEditor.prototype, "setAddressLookupField", null);
//# sourceMappingURL=form.field.editor.js.map